import React, { useRef } from 'react'
import { Dropdown } from 'react-bootstrap'
import { useRole } from '@ztechapps/common.hook'
import { ShortcutDefaults } from '../../../config/shortcutDefaults'
import { Icon, useKeyboardShortcutContext, formatShortcutTitle } from '@ztechapps/webui.util'
import { MenuPopUpEnum } from '../../../components/header/quick-create/MenuPopUpEnum'
import { DropdownItem } from '../../../components/header/quick-create/DropdownItem'
import { useContext } from './Context'
import { OrderPopUp } from '../../../components/header/quick-create/popup/OrderPopUp'
import { OrderFromConversationPopUp } from '../../../components/header/quick-create/popup/OrderFromConversationPopUp'
import { ProductPopUp } from '../../../components/header/quick-create/popup/ProductPopUp'
import { CustomerPopUp } from '../../../components/header/quick-create/popup/CustomerPopUp'
import { PurchaseOrderPopUp } from './popup/PurchaseOrderPopUp'
import { LogisticsPopUp } from './popup/LogisticsPopUp'
import { WorkOrderPopUp } from './popup/WorkOrderPopUp'
import { ProjectPopUp } from './popup/ProjectPopUp'
import { TaskPopUp } from './popup/TaskPopUp'
import { AdjustmentPopUp } from './popup/AdjustmentPopUp'
import { useQuickCreateShortcut } from './useQuickCreateShortcuts'
import { customerPermissions } from '@ztechapps/customer.common'
import { orderPermissions } from '@ztechapps/order.common'
import { productPermissions } from '@ztechapps/product.common'
import { projectPermissions } from '@ztechapps/project.common'
import { useCanCreateOrderFromConversation, orderFromConversationDeniedTitle } from '@ztechapps/order.webui'

export const Layout = () => {
  const { open, setOpen, setMenuTarget, menuTarget } = useContext()
  const orderPermission = useRole({ roleName: orderPermissions.canEdit.value })
  const customerPermission = useRole({ roleName: customerPermissions.canEdit.value })
  const productPermission = useRole({ roleName: productPermissions.canEdit.value })
  const projectPermission = useRole({ roleName: projectPermissions.canEdit.value })
  const toggleRef = useRef<HTMLButtonElement>(null)
  const { getResolvedKeys } = useKeyboardShortcutContext()
  const { canCreateOrderFromConversation } = useCanCreateOrderFromConversation()

  useQuickCreateShortcut({
    shortcut: ShortcutDefaults.QuickCreateOrder,
    menuTarget: MenuPopUpEnum.orderCustomer,
    hasPermission: orderPermission.hasRole,
    toggleRef,
  })
  useQuickCreateShortcut({
    shortcut: ShortcutDefaults.QuickCreateOrderFromConversation,
    menuTarget: MenuPopUpEnum.orderFromConversation,
    hasPermission: canCreateOrderFromConversation,
    toggleRef,
  })
  useQuickCreateShortcut({
    shortcut: ShortcutDefaults.QuickCreateProduct,
    menuTarget: MenuPopUpEnum.product,
    hasPermission: productPermission.hasRole,
    toggleRef,
  })
  useQuickCreateShortcut({
    shortcut: ShortcutDefaults.QuickCreateCustomer,
    menuTarget: MenuPopUpEnum.customer,
    hasPermission: customerPermission.hasRole,
    toggleRef,
  })
  useQuickCreateShortcut({
    shortcut: ShortcutDefaults.QuickCreatePurchase,
    menuTarget: MenuPopUpEnum.purchase,
    hasPermission: orderPermission.hasRole,
    toggleRef,
  })
  useQuickCreateShortcut({
    shortcut: ShortcutDefaults.QuickCreateLogistics,
    menuTarget: MenuPopUpEnum.logistics,
    hasPermission: orderPermission.hasRole,
    toggleRef,
  })
  useQuickCreateShortcut({
    shortcut: ShortcutDefaults.QuickCreateWorkorder,
    menuTarget: MenuPopUpEnum.workOrder,
    hasPermission: projectPermission.hasRole,
    toggleRef,
  })
  useQuickCreateShortcut({
    shortcut: ShortcutDefaults.QuickCreateProject,
    menuTarget: MenuPopUpEnum.project,
    hasPermission: projectPermission.hasRole,
    toggleRef,
  })
  useQuickCreateShortcut({
    shortcut: ShortcutDefaults.QuickCreateTask,
    menuTarget: MenuPopUpEnum.task,
    hasPermission: projectPermission.hasRole,
    toggleRef,
  })
  useQuickCreateShortcut({
    shortcut: ShortcutDefaults.QuickCreateAdjustment,
    menuTarget: MenuPopUpEnum.adjustment,
    hasPermission: productPermission.hasRole,
    toggleRef,
  })

  return (
    <>
      <Dropdown
        align="end"
        id="dropdown-topheader-actions"
        onClick={() => {
          setOpen((current) => !current)
          setMenuTarget({ show: null, target: null })
        }}
        onToggle={(nextShow: boolean, { source }) => {
          if (!menuTarget.show && source === 'rootClose') {
            setOpen(false)
          }
        }}
        onSelect={(eventKey, event) => {
          event.stopPropagation()
          setMenuTarget({ target: event?.target, show: eventKey as MenuPopUpEnum })
        }}
        show={open}
      >
        <Dropdown.Toggle ref={toggleRef}>
          <Icon name="plus" /> Add new
        </Dropdown.Toggle>

        <Dropdown.Menu>
          <DropdownItem
            name={MenuPopUpEnum.orderCustomer}
            disabled={!orderPermission.hasRole}
            menuTarget={menuTarget}
            title={formatShortcutTitle('Create new', getResolvedKeys(ShortcutDefaults.QuickCreateOrder.id))}
          >
            Order from customer...
          </DropdownItem>

          <DropdownItem
            name={MenuPopUpEnum.orderFromConversation}
            disabled={!canCreateOrderFromConversation}
            menuTarget={menuTarget}
            title={
              canCreateOrderFromConversation
                ? formatShortcutTitle(
                    'Create new',
                    getResolvedKeys(ShortcutDefaults.QuickCreateOrderFromConversation.id)
                  )
                : orderFromConversationDeniedTitle
            }
          >
            Order from conversation...
          </DropdownItem>

          <DropdownItem
            name={MenuPopUpEnum.product}
            disabled={!productPermission.hasRole}
            menuTarget={menuTarget}
            title={formatShortcutTitle('Create new', getResolvedKeys(ShortcutDefaults.QuickCreateProduct.id))}
          >
            Product...
          </DropdownItem>

          <DropdownItem
            name={MenuPopUpEnum.customer}
            disabled={!customerPermission.hasRole}
            menuTarget={menuTarget}
            title={formatShortcutTitle('Create new', getResolvedKeys(ShortcutDefaults.QuickCreateCustomer.id))}
          >
            Customer...
          </DropdownItem>
          <DropdownItem
            name={MenuPopUpEnum.purchase}
            disabled={!orderPermission.hasRole}
            menuTarget={menuTarget}
            title={formatShortcutTitle('Create new', getResolvedKeys(ShortcutDefaults.QuickCreatePurchase.id))}
          >
            Purchase...
          </DropdownItem>
          <DropdownItem
            name={MenuPopUpEnum.logistics}
            disabled={!orderPermission.hasRole}
            menuTarget={menuTarget}
            title={formatShortcutTitle('Create new', getResolvedKeys(ShortcutDefaults.QuickCreateLogistics.id))}
          >
            Logistics ...
          </DropdownItem>
          <DropdownItem
            name={MenuPopUpEnum.workOrder}
            disabled={!projectPermission.hasRole}
            menuTarget={menuTarget}
            title={formatShortcutTitle('Create new', getResolvedKeys(ShortcutDefaults.QuickCreateWorkorder.id))}
          >
            Production ...
          </DropdownItem>
          <DropdownItem
            name={MenuPopUpEnum.project}
            disabled={!projectPermission.hasRole}
            menuTarget={menuTarget}
            title={formatShortcutTitle('Create new', getResolvedKeys(ShortcutDefaults.QuickCreateProject.id))}
          >
            Project ...
          </DropdownItem>
          <DropdownItem
            name={MenuPopUpEnum.task}
            disabled={!projectPermission.hasRole}
            menuTarget={menuTarget}
            title={formatShortcutTitle('Create new', getResolvedKeys(ShortcutDefaults.QuickCreateTask.id))}
          >
            Task...
          </DropdownItem>
          <DropdownItem
            name={MenuPopUpEnum.adjustment}
            disabled={!productPermission.hasRole}
            menuTarget={menuTarget}
            title={formatShortcutTitle('Create new', getResolvedKeys(ShortcutDefaults.QuickCreateAdjustment.id))}
          >
            Adjustment ...
          </DropdownItem>
        </Dropdown.Menu>
      </Dropdown>

      <OrderPopUp />
      <OrderFromConversationPopUp />
      <ProductPopUp />
      <CustomerPopUp />
      <PurchaseOrderPopUp />
      <LogisticsPopUp />
      <WorkOrderPopUp />
      <ProjectPopUp />
      <TaskPopUp />
      <AdjustmentPopUp />
    </>
  )
}
